import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import { applicationTypeLabels } from "@common/types/closeApplicationType";
import { ClientsWorkOverviewColumn } from "@common/types/views/clientsWorkOverviewView";
import {
    createColumns,
    type DataGridColumn,
    type DataGridColumnOptions,
} from "@design-system/DataGrid";
import { CloseHealth } from "@common/types/closeHealth";
import { formatWorkDate } from "../cells/WorkCloseDateCell";
import { buildWorkEstimateTooltip } from "../cells/workEstimateTooltip";
import { getHealthReasonTooltip } from "../cells/WorkHealthCell";
import { getMyRoles } from "../cells/WorkMyRoleCell";
import {
    clientsWorkOverviewViewConfig,
    type ClientsWorkOverviewRow,
    type ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { WorkTeamListPanel } from "../editing/WorkRolePicker";
import { getWorkOverdueLabel } from "../workOverdue";
import {
    WorkListActionsCell,
    WorkAppliedByListCell,
    WorkDeliveryDateListCell,
    WorkEstimatedTimeListCell,
    WorkHealthChip,
    WorkNameTrailing,
    WorkProgressCell,
} from "./workListCells";
import { buildWorkDateEditor, type WorkListEditing } from "./workListEditing";
import {
    getDaysInStage,
    getHealthLabel,
    getServiceCopyId,
    getServiceCopyLabel,
    getServicePeriod,
    getStage,
    getWorkEstimatedTime,
    getWorkProgress,
    toAppliedBy,
    toWorkTeamMembers,
    workValue,
} from "./workListValues";

export type WorkListColumn = DataGridColumn<ClientsWorkOverviewRow>;

/** The page-wide values the columns read; the table rebuilds them when one changes. */
export interface WorkListColumnInputs {
    members: readonly OrganizationMember[] | undefined;
    userId: number | undefined;
    /** `null` when the user can't update work: no column edits. */
    editing: WorkListEditing | null;
    /** One clock for the whole table, so every row's time in stage agrees. */
    now: Date;
}

type WorkRow = ClientsWorkOverviewWorkRow;
type WorkColumnConfig =
    (typeof clientsWorkOverviewViewConfig.columns)[ClientsWorkOverviewColumn];

interface WorkColumnArgs {
    id: string;
    options: DataGridColumnOptions<ClientsWorkOverviewRow>;
    inputs: WorkListColumnInputs;
}

/** One config column as a DataGrid column, or null when the page doesn't show it. */
type WorkColumnBuilder = (args: WorkColumnArgs) => WorkListColumn | null;

const col = createColumns<ClientsWorkOverviewRow>();

const textColumn =
    (read: (row: WorkRow) => string | null): WorkColumnBuilder =>
    ({ id, options }) =>
        col.text({ id, value: workValue(read) }, options);

const dateColumn =
    (read: (row: WorkRow) => string | null): WorkColumnBuilder =>
    ({ id, options }) =>
        col.date(
            { id, value: workValue(read) },
            { ...options, showYear: true },
        );

/** A client and its work, or a group and its work; work rows are leaves. */
export const getWorkListSubRows = (
    row: ClientsWorkOverviewRow,
): ClientsWorkOverviewRow[] | undefined =>
    row.rowType === "work" ? undefined : row.children;

/** Each shown column's builder, by config id; config columns without one are left out. */
const WORK_LIST_COLUMN_BUILDERS: Partial<Record<string, WorkColumnBuilder>> = {
    [ClientsWorkOverviewColumn.CLIENT]: ({ id, options }) =>
        col.text(
            { id, value: (row) => row.name },
            {
                ...options,
                tree: true,
                pin: "start",
                trailing: WorkNameTrailing,
                copyId: getServiceCopyId,
                copyLabel: getServiceCopyLabel,
            },
        ),
    [ClientsWorkOverviewColumn.WORKSPACE_NAME]: ({ id, options }) =>
        col.text(
            { id, value: workValue((row) => row.workspaceName) },
            {
                ...options,
                copyId: workValue((row) => row.workspaceId),
                copyLabel: "Workspace",
            },
        ),
    [ClientsWorkOverviewColumn.ENTITY]: ({ id, options }) =>
        col.entity(
            {
                id,
                value: workValue((row) => ({
                    name: row.entityName,
                    iconColor: row.entityIconColor,
                    logo: row.entityLogo,
                })),
            },
            options,
        ),
    [ClientsWorkOverviewColumn.CLOSE_START]: dateColumn((row) => row.startDate),
    [ClientsWorkOverviewColumn.CLOSE_END]: dateColumn((row) => row.endDate),
    [ClientsWorkOverviewColumn.SERVICE_PERIOD]: textColumn(getServicePeriod),
    [ClientsWorkOverviewColumn.WORK_START_DATE]: ({ id, options, inputs }) =>
        col.date(
            { id, value: workValue((row) => row.workStartDate) },
            {
                ...options,
                showYear: true,
                edit: buildWorkDateEditor({
                    field: "workStartDate",
                    editing: inputs.editing,
                }),
            },
        ),
    [ClientsWorkOverviewColumn.WORK_DELIVERY_DATE]: ({ id, options, inputs }) =>
        col.date(
            { id, value: workValue((row) => row.workDeliveryDate) },
            {
                ...options,
                showYear: true,
                edit: buildWorkDateEditor({
                    field: "workDeliveryDate",
                    editing: inputs.editing,
                }),
                render: WorkDeliveryDateListCell,
                tooltip: workValue(
                    (row) => getWorkOverdueLabel(row) ?? undefined,
                ),
            },
        ),
    [ClientsWorkOverviewColumn.CLOSE_STAGE]: textColumn(getStage),
    [ClientsWorkOverviewColumn.TIME_IN_STAGE]: ({ id, options, inputs }) =>
        col.text(
            {
                id,
                value: workValue((row) =>
                    getDaysInStage({ row, now: inputs.now }),
                ),
            },
            options,
        ),
    [ClientsWorkOverviewColumn.PREPARER_REVIEWER]: ({ id, options, inputs }) =>
        col.people(
            { id, value: toWorkTeamMembers },
            {
                ...options,
                max: 5,
                edit: inputs.editing
                    ? col.edit.popover({
                          value: toWorkTeamMembers,
                          commit: () => undefined,
                          placeholder: "Assign team",
                          layout: "fit",
                          content: ({ row }) =>
                              row.rowType === "work" ? (
                                  <WorkTeamListPanel row={row} />
                              ) : null,
                      })
                    : undefined,
            },
        ),
    [ClientsWorkOverviewColumn.MY_ROLE]: ({ id, options, inputs }) =>
        col.text(
            {
                id,
                value: workValue(
                    (row) => getMyRoles({ row, userId: inputs.userId }) || null,
                ),
            },
            options,
        ),
    [ClientsWorkOverviewColumn.TASK_PROGRESS]: ({ id, options }) =>
        col.custom({
            ...options,
            id,
            value: workValue(getWorkProgress),
            render: WorkProgressCell,
        }),
    [ClientsWorkOverviewColumn.ESTIMATED_TIME]: ({ id, options }) =>
        col.custom({
            ...options,
            id,
            align: "end",
            value: workValue(getWorkEstimatedTime),
            render: WorkEstimatedTimeListCell,
            tooltip: workValue((row) =>
                getWorkEstimatedTime(row) === undefined
                    ? undefined
                    : buildWorkEstimateTooltip({ row }),
            ),
        }),
    [ClientsWorkOverviewColumn.APPLICATION_TYPE]: textColumn(
        (row) => applicationTypeLabels[row.applicationType],
    ),
    [ClientsWorkOverviewColumn.APPLIED_BY]: ({ id, options, inputs }) =>
        col.person(
            {
                id,
                value: workValue((row) =>
                    toAppliedBy({ row, members: inputs.members }),
                ),
            },
            { ...options, render: WorkAppliedByListCell },
        ),
    [ClientsWorkOverviewColumn.APPLIED_AT]: textColumn((row) =>
        formatWorkDate(row.createdAt),
    ),
    [ClientsWorkOverviewColumn.HEALTH]: ({ id, options }) =>
        col.text(
            { id, value: workValue(getHealthLabel) },
            {
                ...options,
                render: WorkHealthChip,
                tooltip: workValue((row) =>
                    row.health === CloseHealth.NONE
                        ? undefined
                        : getHealthReasonTooltip(row.healthReason),
                ),
            },
        ),
    [ClientsWorkOverviewColumn.UNREAD_TASKS]: ({ id, options }) =>
        col.number(
            {
                id,
                value: workValue((row) =>
                    row.unreadTaskCount > 0 ? row.unreadTaskCount : null,
                ),
            },
            options,
        ),
};

/** What every column takes from its config: header, widths, sorting, hiding, and variants. */
const toColumnOptions = (
    config: WorkColumnConfig,
): DataGridColumnOptions<ClientsWorkOverviewRow> => ({
    header: config.name,
    ariaLabel: config.name,
    size: config.minSize,
    minSize: config.minSize,
    maxSize: config.maxSize,
    fill: 1,
    fillUntilResized: true,
    enableSorting: Boolean(config.sort),
    enableHiding: !config.immutable,
    empty: "blank",
    variants: config.headerAction && {
        defaultValue: config.headerAction.defaultValue,
        options: config.headerAction.options,
    },
});

/**
 * The Work list's DataGrid columns in `clientsWorkOverviewViewConfig` order,
 * between a pinned select column and the pinned row actions. Each starts at
 * its config minimum and shares leftover width up to its maximum; sortable
 * ones sort descending first unless the config says ascending.
 */
export const buildWorkListColumns = (
    inputs: WorkListColumnInputs,
): WorkListColumn[] => [
    col.select({ pin: "start" }),
    ...Object.entries(clientsWorkOverviewViewConfig.columns).flatMap(
        ([id, config]) => {
            const column = WORK_LIST_COLUMN_BUILDERS[id]?.({
                id,
                options: toColumnOptions(config),
                inputs,
            });
            return column
                ? [
                      {
                          ...column,
                          sortDescFirst: config.sort?.defaultOrder !== "asc",
                      },
                  ]
                : [];
        },
    ),
    col.actions({ render: WorkListActionsCell, width: 120, pin: "end" }),
];
